import React from "react";
import { FaStar } from "react-icons/fa";
import { Marquee } from "./magicui/marquee";

type Review = {
  name: string;
  role?: string;
  quote: string;
  avatar?: string;
  sourceUrl?: string;
  stars: number;
};

const reviews: Review[] = [
  {
    name: "Alex",
    quote:
      "Super cool idea. Setting up payments and auth for Chrome extensions is usually a pain. Love how clean and focused this looks. Nice work!",
    avatar:
      "https://ph-avatars.imgix.net/7113572/0f090584-bb74-433e-8439-5df56000682c.jpeg?auto=compress&codec=mozjpeg&cs=strip&auto=format&w=120&h=120&fit=crop&frame=1&dpr=1",
    role: "Co-founder of GeniusGate",
    sourceUrl:
      "https://www.producthunt.com/products/extensionfast?comment=4549322",
    stars: 5,
  },
  {
    name: "Maximilian Øystå Lloyd",
    quote:
      "I'm usually allergic to boilerplates... No unnecessary abstractions, no overengineering. Just the essentials to get you building and shipping.",
    avatar:
      "https://ph-avatars.imgix.net/3525522/7d586c15-0930-4557-8bdb-d0f557dd730e.jpeg?auto=compress&codec=mozjpeg&cs=strip&auto=format&w=120&h=120&fit=crop&frame=1&dpr=1",
    role: "Coder from Oslo",
    sourceUrl:
      "https://www.producthunt.com/products/extensionfast?launch=extensionfast",
    stars: 5,
  },
  {
    name: "Eric Strohmaier",
    quote:
      "For me, as a not-experienced Chrome store developer, I found it helpful for fast learning and jump-starting the development phase",
    role: "Founder of Prompt Sloth",
    sourceUrl:
      "https://www.producthunt.com/products/extensionfast?launch=extensionfast",
    stars: 5,
  },
  {
    name: "Phil Blouch",
    role: "Founder",
    quote:
      "ExtensionFast has been an awesome starting point for my latest extension build. Definitely worth the money... I'd recommend it to anyone kicking off a fresh extension project!",
    stars: 5,
  },
  {
    name: "Joe Elia",
    quote: "Wow I never seen an extension boilerplate with Stripe",
    avatar:
      "https://ph-avatars.imgix.net/8192716/original.jpeg?auto=compress&codec=mozjpeg&cs=strip&auto=format&w=120&h=120&fit=crop&frame=1&dpr=1",
    role: "Founder",
    sourceUrl:
      "https://www.producthunt.com/products/extensionfast?comment=4585133",
    stars: 4,
  },
  {
    name: "Oz",
    quote:
      "Definitely a time-saver if you want to build nice extensions that are ready for production / customers",
    avatar:
      "https://ph-avatars.imgix.net/7973597/c6693112-8cae-4a05-a273-f41c06ae6c23.png?auto=compress&codec=mozjpeg&cs=strip&auto=format&w=120&h=120&fit=crop&frame=1&dpr=1",
    role: "Digital Nomad",
    sourceUrl:
      "https://www.producthunt.com/products/extensionfast?comment=4591101",
    stars: 5,
  },
];

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const firstRow = reviews.slice(0, reviews.length / 2);
const secondRow = reviews.slice(reviews.length / 2);

const ReviewCard: React.FC<{ review: Review }> = ({ review }) => (
  <div className="w-[350px] flex-shrink-0 bg-panel border border-line rounded-2xl p-6 shadow-lg shadow-black/20 hover:border-zinc-700 transition-all duration-300 relative">
    <span className="absolute top-4 right-5 text-5xl leading-none font-serif text-white/10 select-none pointer-events-none">
      &ldquo;
    </span>

    <div className="flex mb-3 gap-0.5">
      {Array.from({ length: review.stars }).map((_, i) => (
        <FaStar key={i} className="text-amber-400 text-sm" />
      ))}
    </div>

    <p className="text-zinc-300 text-[15px] leading-relaxed mb-5">
      &ldquo;{review.quote}&rdquo;
    </p>

    <div className="flex items-center gap-3 mt-auto">
      {review.avatar ? (
        <img
          src={review.avatar}
          alt={review.name}
          className="w-10 h-10 rounded-full object-cover ring-2 ring-line"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center text-xs font-semibold text-primary tracking-wide ring-2 ring-line">
          {getInitials(review.name)}
        </div>
      )}
      <div>
        <p className="text-white font-medium text-sm">{review.name}</p>
        {review.role && (
          <p className="text-zinc-500 text-xs">{review.role}</p>
        )}
      </div>
    </div>
  </div>
);

const ReviewsSection: React.FC = () => {
  return (
    <section className="py-8">
      <div className="mx-auto relative overflow-hidden flex flex-col gap-y-4 justify-center items-center">
        <div className="pointer-events-none absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-ink to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-ink to-transparent z-10" />

        <Marquee pauseOnHover className="[--duration:35s] [--gap:1.5rem]">
          {firstRow.map((review, idx) => (
            <ReviewCard key={idx} review={review} />
          ))}
        </Marquee>

        <Marquee reverse pauseOnHover className="[--duration:35s] [--gap:1.5rem]">
          {secondRow.map((review, idx) => (
            <ReviewCard key={idx} review={review} />
          ))}
        </Marquee>
      </div>
    </section>
  );
};

export default ReviewsSection;
